import { Footer } from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, CheckCircle2, Code, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import datadogLogo from "@/assets/datadog-logo.png";
import { WaitlistCTA } from "@/components/WaitlistCTA";

const Datadog = () => {

  return (
    <main className="min-h-screen">
      <ThemeToggle />
      
      {/* Header */}
      <section className="py-8 px-4 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </section>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                Fix Datadog Bugs with Healion
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Transform Datadog alerts into automated fixes with intelligent prioritization and instant PR generation
              </p>
            </div>
            <div className="relative h-[300px] rounded-xl overflow-hidden bg-background border border-border flex items-center justify-center p-8">
              <img src={datadogLogo} alt="Datadog Logo" className="w-full h-full object-contain dark:invert" />
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">How It Works</h2>
          
          <div className="space-y-8">
            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">1</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Datadog Monitors Your Stack</h3>
                  <p className="text-muted-foreground">
                    Datadog continuously monitors your infrastructure, applications, and logs across your entire stack, detecting anomalies and errors.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">2</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Webhook Notification</h3>
                  <p className="text-muted-foreground">
                    When a monitor triggers an alert, Datadog sends a webhook to Healion with detailed metrics, traces, and log context.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">3</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Intelligent Triage</h3>
                  <p className="text-muted-foreground">
                    Healion's AI analyzes alert severity, affected services, and historical patterns to prioritize fixes based on business impact.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">4</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Root Cause Analysis</h3>
                  <p className="text-muted-foreground">
                    Using Datadog's metrics and traces, the AI identifies the root cause and generates a targeted fix that addresses the issue.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-accent">5</span>
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">PR with Full Context</h3>
                  <p className="text-muted-foreground">
                    A pull request is created with the fix, including links to Datadog dashboards and the metrics that triggered the alert.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Setup Instructions */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Setup Integration</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-accent" />
                Step 1: Create Webhook Integration
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Open Datadog and navigate to Integrations → Integrations</li>
                  <li>Search for "Webhooks" and click Configure</li>
                  <li>Click "New" to create a new webhook</li>
                  <li>Name it "Healion" and enter the webhook URL:</li>
                </ol>
                <div className="mt-4 p-4 bg-background rounded-md font-mono text-sm border border-border">
                  https://api.healion.ai/webhooks/datadog
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Payload format: <span className="font-semibold">JSON</span>
                </p>
              </Card>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />
                Step 2: Configure Monitor Notifications
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Go to Monitors → Manage Monitors</li>
                  <li>Select an existing monitor or create a new one</li>
                  <li>In the "Notify your team" section, add:</li>
                </ol>
                <div className="mt-4 p-4 bg-background rounded-md font-mono text-sm border border-border">
                  @webhook-Healion
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  You can add this to any monitor that should trigger automatic fixes
                </p>
              </Card>
            </div>

            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />
                Step 3: Verify Integration
              </h3>
              <Card className="p-6 bg-muted/50">
                <p className="text-muted-foreground mb-4">
                  Test the integration by triggering a monitor:
                </p>
                <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                  <li>Monitor triggers and sends webhook to Healion</li>
                  <li>Check Healion dashboard for received alert</li>
                  <li>Verify PR is created with fix and Datadog links</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Key Benefits</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Full Stack Visibility</h3>
              <p className="text-muted-foreground">
                Leverage Datadog's comprehensive monitoring data for accurate root cause analysis.
              </p>
            </Card>

            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Metric-Driven Fixes</h3>
              <p className="text-muted-foreground">
                AI uses performance metrics and traces to validate fixes before creating PRs.
              </p>
            </Card>

            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Reduce Alert Fatigue</h3>
              <p className="text-muted-foreground">
                Automatically resolve common issues, letting your team focus on complex problems.
              </p>
            </Card>

            <Card className="p-6">
              <CheckCircle className="w-8 h-8 text-accent mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">Seamless Workflow</h3>
              <p className="text-muted-foreground">
                Works with existing monitors—no changes to your Datadog setup required.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-8">
            Connect Datadog to Healion and start fixing issues automatically
          </p>
          
          <WaitlistCTA source="Datadog Page" layout="stacked" />
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Datadog;